import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuizCompletionModal = ({ 
  isOpen = false,
  score = 0,
  totalQuestions = 10,
  correctAnswers = 0,
  timeSpent = 0,
  passingScore = 70,
  onClose = () => {},
  onViewResults = () => {},
  onRetakeQuiz = () => {}
}) => {
  const navigate = useNavigate();
  
  if (!isOpen) return null;

  const percentage = Math.round((correctAnswers / totalQuestions) * 100);
  const isPassed = percentage >= passingScore;
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getScoreColor = () => {
    if (percentage >= 90) return 'success';
    if (percentage >= passingScore) return 'secondary';
    return 'warning';
  };

  const getScoreIcon = () => {
    if (percentage >= 90) return 'Trophy';
    if (percentage >= passingScore) return 'CheckCircle2';
    return 'AlertCircle';
  };

  const getEncouragementMessage = () => {
    if (percentage >= 95) return "Outstanding performance! You've mastered this material.";
    if (percentage >= 85) return "Excellent work! You have a strong understanding.";
    if (percentage >= passingScore) return "Good job! You've successfully passed this assessment.";
    return "Keep learning! Review the material and try again.";
  };

  const handleViewDetailedResults = () => {
    navigate('/quiz-results-performance-analytics/');
  };

  const handleBackToDashboard = () => {
    navigate('/chapter-overview-dashboard');
  };

  return (
    <div className="fixed inset-0 overflow-y-auto z-300">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background Overlay */}
        <div className="fixed inset-0 transition-opacity bg-opacity-50 bg-text-primary" />

        {/* Modal */}
        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform shadow-xl bg-surface rounded-2xl">
          {/* Header */}
          <div className="mb-6 text-center">
            <div className={`
              flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full
              ${getScoreColor() === 'success' ? 'bg-success-50' :
                getScoreColor() === 'secondary' ? 'bg-secondary-50' : 'bg-warning-50'}
            `}>
              <Icon 
                name={getScoreIcon()} 
                size={28} 
                color={`var(--color-${getScoreColor()})`}
                strokeWidth={2}
              />
            </div>
            
            <h3 className="mb-2 text-2xl font-bold font-heading text-text-primary">
              Quiz Complete!
            </h3>
            
            <div className="mb-1 text-3xl font-bold font-heading text-text-primary">
              {percentage}%
            </div>
            
            <p className="text-text-secondary">
              {correctAnswers} of {totalQuestions} questions correct
            </p>
          </div>

          {/* Status Badge */}
          <div className="flex justify-center mb-4">
            <div className={`
              inline-flex items-center space-x-2 px-4 py-2 rounded-full
              ${isPassed 
                ? 'bg-success-50 text-success-700' :'bg-warning-50 text-warning-700'}
            `}>
              <Icon 
                name={isPassed ? "CheckCircle2" : "XCircle"} 
                size={16} 
                color="currentColor"
                strokeWidth={2}
              />
              <span className="text-sm font-medium">
                {isPassed ? 'Passed' : 'Not Passed'} • {passingScore}% required
              </span>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-3 text-center rounded-lg bg-surface-secondary">
              <div className="text-lg font-bold font-heading text-text-primary">
                {percentage}%
              </div>
              <div className="text-xs text-text-secondary">Accuracy</div>
            </div>
            <div className="p-3 text-center rounded-lg bg-surface-secondary">
              <div className="text-lg font-bold font-heading text-text-primary">
                {formatTime(timeSpent)}
              </div>
              <div className="text-xs text-text-secondary">Time Spent</div>
            </div>
          </div>

          {/* Encouragement Message */}
          <div className="mb-6 text-center">
            <p className="text-sm text-text-secondary">
              {getEncouragementMessage()}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              variant="primary"
              size="md"
              fullWidth
              iconName="BarChart3"
              iconPosition="left"
              onClick={handleViewDetailedResults}
            >
              View Detailed Results
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="RotateCcw"
                iconPosition="left"
                onClick={onRetakeQuiz}
              >
                Retake Quiz
              </Button>

              <Button
                variant="ghost"
                size="sm"
                fullWidth
                iconName="Home"
                iconPosition="left"
                onClick={handleBackToDashboard}
              >
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizCompletionModal;